// utils/pdfGenerator.js
import jsPDF from 'jspdf';

export const generatePDFContract = async (data) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.width;
  const pageHeight = doc.internal.pageSize.height;
  const margins = { top: 20, left: 20, right: 20, bottom: 20 };
  const contentWidth = pageWidth - margins.left - margins.right;
  
  let currentY = margins.top;
  
  // Configuração de fontes
  doc.setFont('helvetica');
  
  // Função para adicionar nova página se necessário
  const checkPageBreak = (spaceNeeded = 10) => {
    if (currentY + spaceNeeded > pageHeight - margins.bottom) {
      doc.addPage();
      currentY = margins.top;
    }
  };
  
  // Função para formatar moeda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };
  
  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR');
  };
  
  // Calcular total
  const calculateTotal = () => {
    const productsTotal = data.products.reduce(
      (total, product) => total + (Number(product.totalPrice) || 0), 
      0
    );
    const deliveryFee = Number(data.delivery?.fee) || 0;
    return productsTotal + deliveryFee;
  };
  
  // Título
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const title = 'CONTRATO DE COMPRA E VENDA';
  const titleWidth = doc.getTextWidth(title);
  doc.text(title, (pageWidth - titleWidth) / 2, currentY);
  currentY += 20;
  
  // Texto inicial
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  
  const introText = [
    'Pelo presente instrumento particular de compromisso de compra e venda, de um lado',
    'COOPERATIVA DE PRODUTOS EXTRATIVISTA DO DELTA - COOPDELTA, com sede',
    'na cidade de Araioses-MA, Rua Beira Mar S/N, Povoado Carnaubeiras, Araioses, MA,',
    'CEP 65570-000, inscrita no CNPJ sob o nº 53.034.052/0001-45, de ora adiante chamado de',
    'VENDEDORA, e do outro lado...'
  ];
  
  introText.forEach(line => {
    checkPageBreak();
    doc.text(line, margins.left, currentY);
    currentY += 5;
  });
  
  currentY += 5;
  
  // Dados do comprador
  if (data.buyer?.type === 'pf') {
    const buyerText = `${data.buyer.name || 'Sr. _______________'}, ${data.buyer.address ? `residente na ${data.buyer.address},` : ''} ${data.buyer.city && data.buyer.state ? `${data.buyer.city}-${data.buyer.state}` : ''} ${data.buyer.cpf ? `portador do CPF nº ${data.buyer.cpf},` : ''} doravante denominado(a) COMPRADOR, têm, entre si, como justo e contratado o que se segue:`;
    
    const splitText = doc.splitTextToSize(buyerText, contentWidth);
    splitText.forEach(line => {
      checkPageBreak();
      doc.text(line, margins.left, currentY);
      currentY += 5;
    });
  } else {
    const buyerText = `${data.buyer?.companyName || 'Empresa _______________'}, ${data.buyer?.address ? `com sede na ${data.buyer.address},` : ''} ${data.buyer?.city && data.buyer?.state ? `${data.buyer.city}-${data.buyer.state},` : ''} ${data.buyer?.cnpj ? `inscrita no CNPJ sob o nº ${data.buyer.cnpj},` : ''} representada pelo Sr. ${data.buyer?.representative || '_______________'}, ${data.buyer?.representativeCpf ? `portador do CPF nº ${data.buyer.representativeCpf},` : ''} doravante denominado(a) COMPRADOR, têm, entre si, como justo e contratado o que se segue:`;
    
    const splitText = doc.splitTextToSize(buyerText, contentWidth);
    splitText.forEach(line => {
      checkPageBreak();
      doc.text(line, margins.left, currentY);
      currentY += 5;
    });
  }
  
  currentY += 10;
  
  // CLÁUSULA PRIMEIRA
  checkPageBreak(20);
  doc.setFont('helvetica', 'bold');
  doc.text('CLÁUSULA PRIMEIRA – DO OBJETO', margins.left, currentY);
  currentY += 8;
  
  doc.setFont('helvetica', 'normal');
  const clauseOneText = `A VENDEDORA vende ao COMPRADOR, pelo preço certo e ajustado de ${formatCurrency(calculateTotal())} o seguinte:`;
  const splitClauseOne = doc.splitTextToSize(clauseOneText, contentWidth);
  splitClauseOne.forEach(line => {
    checkPageBreak();
    doc.text(line, margins.left, currentY);
    currentY += 5;
  });
  
  currentY += 10;
  
  // Tabela de produtos
  if (data.products && data.products.length > 0) {
    checkPageBreak(30);
    
    // Cabeçalho da tabela
    doc.setFont('helvetica', 'bold');
    doc.text('Item', margins.left, currentY);
    doc.text('Qnt/Uni', margins.left + 30, currentY);
    doc.text('Especificação', margins.left + 70, currentY);
    doc.text('Preço Unit.', margins.left + 120, currentY);
    doc.text('Preço Total', margins.left + 160, currentY);
    currentY += 8;
    
    // Linha separadora
    doc.line(margins.left, currentY - 2, pageWidth - margins.right, currentY - 2);
    
    doc.setFont('helvetica', 'normal');
    
    // Produtos
    data.products.forEach((product, index) => {
      checkPageBreak();
      doc.text(`${index + 1}`, margins.left, currentY);
      doc.text(`${product.quantity} ${product.unit}`, margins.left + 30, currentY);
      doc.text(product.name, margins.left + 70, currentY);
      doc.text(formatCurrency(Number(product.unitPrice) || 0), margins.left + 120, currentY);
      doc.text(formatCurrency(Number(product.totalPrice) || 0), margins.left + 160, currentY);
      currentY += 6;
    });
    
    // Total
    currentY += 5;
    doc.line(margins.left, currentY - 2, pageWidth - margins.right, currentY - 2);
    doc.setFont('helvetica', 'bold');
    doc.text('Valor total:', margins.left + 120, currentY);
    doc.text(formatCurrency(calculateTotal()), margins.left + 160, currentY);
    currentY += 10;
  }
  
  // CLÁUSULA SEGUNDA - GARANTIA
  checkPageBreak(20);
  doc.setFont('helvetica', 'bold');
  doc.text('CLÁUSULA SEGUNDA – GARANTIA', margins.left, currentY);
  currentY += 8;
  
  doc.setFont('helvetica', 'normal');
  const guaranteeText = 'Os produtos vendidos estão de acordo com as normas sanitárias vigentes e com as condições de conservação informadas no ato da venda. A garantia refere-se exclusivamente à qualidade do produto entregue, sendo a VENDEDORA responsável por eventuais defeitos de origem constatados no momento da entrega, conforme artigo 50 do Código de Defesa do Consumidor.';
  const splitGuarantee = doc.splitTextToSize(guaranteeText, contentWidth);
  splitGuarantee.forEach(line => {
    checkPageBreak();
    doc.text(line, margins.left, currentY);
    currentY += 5;
  });
  
  currentY += 10;
  
  // CLÁUSULA TERCEIRA - PAGAMENTO
  checkPageBreak(20);
  doc.setFont('helvetica', 'bold');
  const paymentTitle = `CLÁUSULA TERCEIRA – PAGAMENTO ${data.payment?.type === 'prazo' ? '(A PRAZO)' : '(À VISTA)'}`;
  doc.text(paymentTitle, margins.left, currentY);
  currentY += 8;
  
  doc.setFont('helvetica', 'normal');
  
  if (data.payment?.type === 'prazo') {
    const paymentText = `O valor total da compra, ${formatCurrency(calculateTotal())}, será pago a prazo de forma parcelada:`;
    const splitPayment = doc.splitTextToSize(paymentText, contentWidth);
    splitPayment.forEach(line => {
      checkPageBreak();
      doc.text(line, margins.left, currentY);
      currentY += 5;
    });
    
    currentY += 5;
    
    // Tabela de parcelas
    if (data.payment?.installments) {
      data.payment.installments.forEach((installment, index) => {
        checkPageBreak();
        doc.text(`Parcela ${index + 1}: ${formatCurrency(Number(installment.amount) || 0)} - ${formatDate(installment.date)}`, margins.left + 10, currentY);
        currentY += 6;
      });
    }
  } else {
    const paymentText = `O pagamento do objeto contratual será realizado à vista, no valor total de ${formatCurrency(calculateTotal())}, correspondente aos produtos descritos na Cláusula Primeira.`;
    const splitPayment = doc.splitTextToSize(paymentText, contentWidth);
    splitPayment.forEach(line => {
      checkPageBreak();
      doc.text(line, margins.left, currentY);
      currentY += 5;
    });
  }
  
  currentY += 10;
  
  // CLÁUSULA QUARTA - ENTREGA
  checkPageBreak(20);
  doc.setFont('helvetica', 'bold');
  doc.text('CLÁUSULA QUARTA – RETIRADA/ENTREGA', margins.left, currentY);
  currentY += 8;
  
  doc.setFont('helvetica', 'normal');
  
  const deliveryText = data.delivery?.type === 'retirada' 
    ? `Os produtos adquiridos serão retirados pelo COMPRADOR ${data.payment?.type === 'vista' ? 'no ato da compra, após a efetiva comprovação do pagamento integral.' : 'no ato da compra, após a assinatura do contrato.'}`
    : 'Os produtos adquiridos serão entregues posteriormente, conforme encomenda. A entrega será realizada no endereço informado pelo COMPRADOR, conforme combinado previamente entre as partes.';
  
  const splitDelivery = doc.splitTextToSize(deliveryText, contentWidth);
  splitDelivery.forEach(line => {
    checkPageBreak();
    doc.text(line, margins.left, currentY);
    currentY += 5;
  });
  
  // Adicionar endereço de entrega se aplicável
  if (data.delivery?.type === 'entrega' && data.delivery?.address) {
    currentY += 5;
    doc.text(`Endereço: ${data.delivery.address}`, margins.left, currentY);
    currentY += 5;
    if (data.delivery.zipCode) {
      doc.text(`CEP: ${data.delivery.zipCode}`, margins.left, currentY);
      currentY += 5;
    }
  }
  
  currentY += 10;
  
  // CLÁUSULAS FINAIS (resumidas)
  const finalClauses = [
    'CLÁUSULA QUINTA – IMPONTUALIDADE DO PAGAMENTO',
    'O pagamento não realizado pelo COMPRADOR na data pactuada incidirá em: a) multa penal de 2% sobre o valor; b) correção monetária de acordo com o índice IPCA; c) juros de 2% ao mês.',
    '',
    'CLÁUSULA SEXTA – INADIMPLEMENTO CONTRATUAL',
    'Ocorrendo a inadimplência do pagamento, o COMPRADOR será responsável por todas as despesas de cobrança extrajudicial e judicial.',
    '',
    'CLÁUSULA SÉTIMA – FORO',
    'As partes elegem o foro da comarca de Araioses-MA para dirimir as questões oriundas deste contrato.'
  ];
  
  finalClauses.forEach(clause => {
    if (clause === '') {
      currentY += 5;
      return;
    }
    
    checkPageBreak(15);
    
    if (clause.startsWith('CLÁUSULA')) {
      doc.setFont('helvetica', 'bold');
      doc.text(clause, margins.left, currentY);
      currentY += 8;
    } else {
      doc.setFont('helvetica', 'normal');
      const splitClause = doc.splitTextToSize(clause, contentWidth);
      splitClause.forEach(line => {
        checkPageBreak();
        doc.text(line, margins.left, currentY);
        currentY += 5;
      });
      currentY += 5;
    }
  });
  
  // Data e assinaturas
  checkPageBreak(50);
  currentY += 10;
  
  doc.setFont('helvetica', 'normal');
  const today = new Date();
  const dateText = `Araioses - MA, ${today.getDate()} de ${today.toLocaleDateString('pt-BR', { month: 'long' })} de ${today.getFullYear()}.`;
  const dateWidth = doc.getTextWidth(dateText);
  doc.text(dateText, (pageWidth - dateWidth) / 2, currentY);
  
  currentY += 30;
  
  // Assinaturas
  const buyerName = data.buyer?.type === 'pf' 
    ? (data.buyer.name || '_______________') 
    : (data.buyer?.representative || '_______________');
  
  // Comprador
  doc.line(margins.left + 20, currentY, margins.left + 80, currentY);
  currentY += 5;
  doc.text(buyerName, margins.left + 50 - doc.getTextWidth(buyerName) / 2, currentY);
  currentY += 5;
  if (data.buyer?.type === 'pf') {
    doc.text(`CPF: ${data.buyer.cpf || '_______________'}`, margins.left + 50 - doc.getTextWidth(`CPF: ${data.buyer.cpf || '_______________'}`) / 2, currentY);
  } else {
    doc.text(data.buyer?.companyName || '_______________', margins.left + 50 - doc.getTextWidth(data.buyer?.companyName || '_______________') / 2, currentY);
  }
  currentY += 5;
  doc.setFont('helvetica', 'bold');
  doc.text('COMPRADOR', margins.left + 50 - doc.getTextWidth('COMPRADOR') / 2, currentY);
  
  currentY += 20;
  
  // Vendedor
  doc.setFont('helvetica', 'normal');
  doc.line(margins.left + 20, currentY, margins.left + 80, currentY);
  currentY += 5;
  doc.text('Paulo Alves Menezes', margins.left + 50 - doc.getTextWidth('Paulo Alves Menezes') / 2, currentY);
  currentY += 5;
  doc.text('Presidente da Coopdelta', margins.left + 50 - doc.getTextWidth('Presidente da Coopdelta') / 2, currentY);
  currentY += 5;
  doc.setFont('helvetica', 'bold');
  doc.text('VENDEDORA', margins.left + 50 - doc.getTextWidth('VENDEDORA') / 2, currentY);
  
  // Gerar nome do arquivo
  const fileName = `Contrato_${data.buyer?.type === 'pf' ? data.buyer?.name?.replace(/\s+/g, '_') : data.buyer?.companyName?.replace(/\s+/g, '_')}_${today.getDate()}-${today.getMonth() + 1}-${today.getFullYear()}.pdf`;
  
  // Fazer download
  doc.save(fileName || 'contrato.pdf');
};

export default generatePDFContract;